import type { ICreateEmptyBrandResponse } from '@/api/brands/mutations/create-empty-brand-mutation'
import { Route, route } from '@/setup/routing/routing'

export type IOnboardingRecoveryStatus = 'idle' | 'pending' | 'error' | 'navigating'

type IRecoveryTarget = {
    emptyBrand: Pick<ICreateEmptyBrandResponse['emptyBrand'], 'uuid'>
    agent: Pick<ICreateEmptyBrandResponse['agent'], 'uuid'>
}

interface IOnboardingRecoveryDependencies {
    getChatUrl: () => string | null
    canRecover: () => boolean
    isCurrentScope: () => boolean
    createIdempotencyKey: () => string
    recover: (idempotencyKey: string) => Promise<IRecoveryTarget>
    navigate: (url: string) => void
    setStatus: (status: IOnboardingRecoveryStatus) => void
}

/** A failed or unfinished read is never evidence that a brand needs a new onboarding chat. */
export function canRecoverOnboardingChat(params: {
    isSidebarLocked: boolean
    emptyBrand: boolean | undefined
    agentsStatus: 'pending' | 'error' | 'success'
    isFetching: boolean
    agentCount: number
}) {
    return (
        params.isSidebarLocked &&
        params.emptyBrand === true &&
        params.agentsStatus === 'success' &&
        !params.isFetching &&
        params.agentCount === 0
    )
}

/**
 * One click-owned recovery attempt. Retries keep their key, concurrent clicks cannot submit twice,
 * and an old workspace/brand response cannot navigate the newly selected workspace/brand.
 */
export function createOnboardingChatRecovery(deps: IOnboardingRecoveryDependencies) {
    let inFlight = false
    let idempotencyKey: string | undefined

    return async () => {
        if (inFlight || !deps.isCurrentScope()) return

        const chatUrl = deps.getChatUrl()
        if (chatUrl) {
            deps.navigate(chatUrl)
            return
        }
        if (!deps.canRecover()) return

        inFlight = true
        deps.setStatus('pending')
        let isNavigating = false
        try {
            idempotencyKey ??= deps.createIdempotencyKey()
            const target = await deps.recover(idempotencyKey)
            if (!deps.isCurrentScope()) return

            const url = route(Route.AGENT_TASK).getUrl({
                brandSlug: target.emptyBrand.uuid,
                agent_uuid: target.agent.uuid,
            })
            deps.setStatus('navigating')
            deps.navigate(url)
            isNavigating = true
        } catch {
            if (deps.isCurrentScope()) deps.setStatus('error')
        } finally {
            // Keep the action closed while the browser begins the full navigation.
            inFlight = isNavigating
        }
    }
}
